import React, { useState, useEffect } from 'react';
import { FiClock, FiCalendar, FiBarChart2, FiEdit3 } from 'react-icons/fi';
import { timeTrackingApi } from '../../services/timeTrackingApi';
import api from '../../services/api';

const TimesheetPage = () => {
  const [sessions, setSessions] = useState([]);
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [totalTime, setTotalTime] = useState(0);
  const [totalManualHours, setTotalManualHours] = useState(0);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchSessions(),
      fetchTimesheetEntries()
    ]);
    setLoading(false);
  };

  const fetchSessions = async () => {
    try {
      console.log('Fetching time tracking sessions for date:', selectedDate);
      const response = await timeTrackingApi.getSessionsByDate(selectedDate);
      console.log('Sessions response:', response.data);
      setSessions(response.data.sessions || []);
      setTotalTime(response.data.total_seconds || 0);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
      setTotalTime(0);
    }
  };

  const fetchTimesheetEntries = async () => {
    try {
      console.log('Fetching timesheet entries...');
      const response = await api.get('/timesheets');
      console.log('Timesheet entries response:', response.data);
      
      // Filter entries for selected date
      const entriesForDate = (response.data || []).filter(entry => {
        const entryDate = new Date(entry.date).toISOString().split('T')[0];
        return entryDate === selectedDate;
      });
      
      setTimesheetEntries(entriesForDate);
      
      // Calculate total manual hours for selected date
      const totalHours = entriesForDate.reduce((sum, entry) => sum + parseFloat(entry.hours), 0);
      setTotalManualHours(totalHours);
      
    } catch (error) {
      console.error('Error fetching timesheet entries:', error);
      setTimesheetEntries([]);
      setTotalManualHours(0);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const getProjectSummary = () => {
    const summary = {};
    
    // Add time tracking sessions
    sessions.forEach(session => {
      const projectName = session.project_name || 'Unknown Project';
      if (!summary[projectName]) {
        summary[projectName] = { seconds: 0, sessions: 0, manualHours: 0, manualEntries: 0 };
      }
      summary[projectName].seconds += session.total_seconds || 0;
      summary[projectName].sessions += 1;
    });
    
    // Add manual timesheet entries
    timesheetEntries.forEach(entry => {
      const projectName = entry.Project?.name || 'Unknown Project';
      if (!summary[projectName]) {
        summary[projectName] = { seconds: 0, sessions: 0, manualHours: 0, manualEntries: 0 };
      }
      summary[projectName].manualHours += parseFloat(entry.hours);
      summary[projectName].manualEntries += 1;
    });
    
    return summary;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Time Tracking Report</h1>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FiCalendar className="text-gray-500" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Combined Total</div>
            <div className="text-xl font-mono font-bold text-purple-600">
              {formatTime(totalTime + (totalManualHours * 3600))}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              <span className="text-blue-600">Auto: {formatTime(totalTime)}</span>
              {totalManualHours > 0 && (
                <><span className="mx-1">•</span>
                <span className="text-green-600">Manual: {formatHours(totalManualHours)}</span></>
              )}
            </div>
          </div>
        </div>
      </div>



      {/* Project Summary */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Project Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(getProjectSummary()).map(([projectName, data]) => (
            <div key={projectName} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-gray-900">{projectName}</h3>
                <FiBarChart2 className="text-blue-500" size={16} />
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                {data.seconds > 0 && (
                  <div className="flex items-center">
                    <FiClock className="mr-1 text-blue-500" size={14} />
                    <span className="text-blue-600">{formatDuration(data.seconds)} ({data.sessions} sessions)</span>
                  </div>
                )}
                {data.manualHours > 0 && (
                  <div className="flex items-center">
                    <FiEdit3 className="mr-1 text-green-500" size={14} />
                    <span className="text-green-600">{formatHours(data.manualHours)} ({data.manualEntries} entries)</span>
                  </div>
                )}
                {data.seconds === 0 && data.manualHours === 0 && (
                  <div className="text-gray-400">No time recorded</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Time Sessions List */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-medium">Automatic Time Tracking Sessions</h2>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              Loading sessions...
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map(session => (
                  <tr key={session.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(session.start_time).toLocaleTimeString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.end_time ? new Date(session.end_time).toLocaleTimeString() : 'Active'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.project_name || 'Unknown Project'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.task_title || 'Unknown Task'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {formatTime(session.total_seconds || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && sessions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No automatic time tracking sessions found for {new Date(selectedDate).toLocaleDateString()}.
            </div>
          )}
        </div>
      </div>

      {/* Manual Time Entries */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-medium flex items-center">
            <FiEdit3 className="mr-2 text-green-500" />
            Manual Time Entries
          </h2>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              Loading entries...
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-green-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timesheetEntries.map(entry => (
                  <tr key={entry.id} className="hover:bg-green-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.Project?.name || 'Unknown Project'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.task_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-green-600">
                      {formatHours(parseFloat(entry.hours))}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {entry.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(entry.created_at).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && timesheetEntries.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No manual time entries found for {new Date(selectedDate).toLocaleDateString()}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimesheetPage;